import {
  useRegisterProfileActivityMutation,
  Variables,
} from "graphql/mutation/register.profile.activity.mutation";
import posthog from "posthog-js";
import { useAppDispatch, useAppSelector } from "store";
import { addTriggers, isTriggerType } from "store/trigger";
import { ID } from "utils/id";

let count = 0;

type PayloadType = {
  [key: string]: string | number | boolean;
};

/** Activity payload as callers pass it; `null` and `undefined` entries are dropped before sending. */
type PayloadInput = {
  [key: string]: string | number | boolean | null | undefined;
};

export const useRegisterActivity = (): ((
  activity: string,
  company?: ID | null,
  payload?: PayloadInput,
) => Promise<void>) => {
  const dispatch = useAppDispatch();
  const authenticated = useAppSelector((state) => state.auth.authenticated);

  const fallbackCompany = useAppSelector((state) => state.company.id);

  const [registerActivity] = useRegisterProfileActivityMutation({});

  return async (activity: string, company?: ID | null, payload?: PayloadInput) => {
    const filteredPayload: PayloadType = {};

    if (payload) {
      for (const [key, value] of Object.entries(payload)) {
        if (value !== null && value !== undefined) {
          filteredPayload[key] = value;
        }
      }
    }

    posthog.capture(activity, {
      ...filteredPayload,
      company: company === undefined ? fallbackCompany : company,
    });

    if (!authenticated) {
      return;
    }

    const variables: Variables = {
      activity,
      company: null,
      payload: payload ? JSON.stringify(filteredPayload) : undefined,
    };

    if (company !== undefined) {
      variables.company = company;
    } else if (fallbackCompany) {
      variables.company = fallbackCompany;
    }

    if (variables.company === "undefined") {
      variables.company = null;
    }

    const { data } = await registerActivity({
      context: {
        batch: false,
      },
      variables,
    });

    const triggers = data?.status?.triggers;

    if (!triggers) {
      return;
    }

    const newTriggers = triggers.flatMap((trigger) =>
      isTriggerType(trigger.type)
        ? [
            {
              id: String(count++),
              payload: trigger.payload ?? undefined,
              type: trigger.type,
            },
          ]
        : [],
    );

    dispatch(addTriggers(newTriggers));
  };
};
